import React, { useEffect, useState } from "react";
import { orgAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, LoadingSpinner, StatCard } from "../../components/ui";

const TABS = ["Branch Comparison", "Bottlenecks", "Staff Performance", "Hourly Heatmap"];
const COLORS = ["#3B82F6","#22C55E","#EAB308","#EF4444","#A78BFA","#F472B6","#34D399","#FB923C"];

export default function OrgReportsPage() {
  const { user } = useAuth();
  const orgId    = user?.organisationId;
  const [tab,        setTab]       = useState(0);
  const [loading,    setLoading]   = useState(false);
  const [data,       setData]      = useState(null);
  const [dateFrom,   setDateFrom]  = useState(thisMonthStart());
  const [dateTo,     setDateTo]    = useState(today());

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    setData(null);
    const params = { date_from: dateFrom, date_to: dateTo };
    const calls  = [
      orgAdmin.reportBranches(orgId, params),
      orgAdmin.reportBottlenecks(orgId, params),
      orgAdmin.reportStaff(orgId, params),
      orgAdmin.reportHourly(orgId, params),
    ];
    Promise.all(calls)
      .then(([br, bot, st, hr]) => setData({ branches: br.data, bottlenecks: bot.data, staff: st.data, hourly: hr.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId, dateFrom, dateTo]);

  return (
    <div style={styles.root}>
      <PageHeader title="Organisation Reports" subtitle="Cross-branch analytics and performance comparison." />

      {/* Date range + tabs */}
      <div style={styles.toolbar}>
        <div style={styles.tabs}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)} style={{ ...styles.tab, ...(tab === i ? styles.tabActive : {}) }}>{t}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input type="date" style={styles.datePicker} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <span style={{ color: "#475569", fontSize: "13px" }}>→</span>
          <input type="date" style={styles.datePicker} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
      </div>

      {loading ? <LoadingSpinner /> : !data ? null : (
        <>
          {tab === 0 && <BranchComparison data={data.branches} />}
          {tab === 1 && <Bottlenecks data={data.bottlenecks} />}
          {tab === 2 && <StaffPerformance data={data.staff} />}
          {tab === 3 && <HourlyHeatmap data={data.hourly} />}
        </>
      )}
    </div>
  );
}

// ── Branch Comparison ─────────────────────────────────────────────────────────
function BranchComparison({ data }) {
  const branches = data?.branches || [];
  const maxTickets = Math.max(...branches.map(b => b.total_tickets), 1);

  return (
    <div style={R.col}>
      {/* Summary stats */}
      <div style={R.statsRow}>
        <StatCard label="Total Tickets" value={branches.reduce((s,b) => s + b.total_tickets, 0)} color="#60A5FA" />
        <StatCard label="Best Completion" value={branches.length ? `${Math.max(...branches.map(b => b.completion_rate))}%` : null} color="#22C55E" />
        <StatCard label="Avg Wait (best)" value={branches.length ? `${Math.min(...branches.filter(b=>b.avg_wait_minutes).map(b => b.avg_wait_minutes))} min` : null} color="#A78BFA" />
      </div>

      {/* Bar chart — tickets */}
      <div style={R.card}>
        <div style={R.cardTitle}>Tickets Served by Branch</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {branches.map((b, i) => (
            <div key={b.branch_id}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#E2E8F0" }}>{b.branch_name}</span>
                <span style={{ fontSize: "13px", color: COLORS[i % COLORS.length], fontWeight: "700" }}>{b.total_tickets}</span>
              </div>
              <div style={R.barTrack}>
                <div style={{ ...R.barFill, width: `${(b.total_tickets / maxTickets) * 100}%`, background: COLORS[i % COLORS.length] }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={R.card}>
        <div style={R.cardTitle}>Detailed Comparison</div>
        <table style={R.table}>
          <thead><tr>
            {["Branch","Tickets","Completed","Abandoned","Completion %","Abandon %","Avg Wait"].map(h => <th key={h} style={R.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {branches.map((b, i) => (
              <tr key={b.branch_id} style={R.tr}>
                <td style={R.td}><span style={{ color: COLORS[i % COLORS.length], fontWeight: "700", marginRight: "8px" }}>●</span><span style={{ color: "#E2E8F0" }}>{b.branch_name}</span></td>
                <td style={{ ...R.td, textAlign: "center" }}>{b.total_tickets}</td>
                <td style={{ ...R.td, textAlign: "center", color: "#22C55E" }}>{b.completed}</td>
                <td style={{ ...R.td, textAlign: "center", color: "#EF4444" }}>{b.abandoned}</td>
                <td style={{ ...R.td, textAlign: "center", color: b.completion_rate >= 80 ? "#22C55E" : b.completion_rate >= 60 ? "#EAB308" : "#EF4444" }}>{b.completion_rate}%</td>
                <td style={{ ...R.td, textAlign: "center" }}>{b.abandonment_rate}%</td>
                <td style={{ ...R.td, textAlign: "center", color: b.avg_wait_minutes > 15 ? "#EF4444" : "#94A3B8" }}>{b.avg_wait_minutes} min</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Bottleneck Analysis ───────────────────────────────────────────────────────
function Bottlenecks({ data }) {
  const items = data?.bottlenecks || [];
  return (
    <div style={R.col}>
      <div style={R.card}>
        <div style={R.cardTitle}>Cross-Branch Bottlenecks — Worst Average Wait Times</div>
        <p style={{ fontSize: "13px", color: "#64748B", marginBottom: "16px" }}>
          Stations with avg wait over 15 minutes are flagged as bottlenecks. Address these first.
        </p>
        <table style={R.table}>
          <thead><tr>
            {["Branch","Station","Visits","Avg Wait","Avg Service","Status"].map(h => <th key={h} style={R.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} style={R.tr}>
                <td style={R.td}><span style={{ fontSize: "13px", color: "#94A3B8" }}>{item.branch_name}</span></td>
                <td style={R.td}><span style={{ fontSize: "13px", fontWeight: "600", color: "#E2E8F0" }}>{item.station_name}</span></td>
                <td style={{ ...R.td, textAlign: "center" }}>{item.total_visits}</td>
                <td style={{ ...R.td, textAlign: "center", color: item.avg_wait_min > 15 ? "#EF4444" : "#22C55E", fontWeight: "700" }}>{item.avg_wait_min} min</td>
                <td style={{ ...R.td, textAlign: "center", color: "#94A3B8" }}>{item.avg_service_min} min</td>
                <td style={{ ...R.td, textAlign: "center" }}>
                  {item.is_bottleneck
                    ? <span style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>⚠ Bottleneck</span>
                    : <span style={{ background: "rgba(34,197,94,0.1)", color: "#22C55E", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>✓ OK</span>
                  }
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} style={{ ...R.td, textAlign: "center", color: "#475569", padding: "32px" }}>No data for this period.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Staff Performance ─────────────────────────────────────────────────────────
function StaffPerformance({ data }) {
  const staff = data?.staff || [];
  const maxServed = Math.max(...staff.map(s => s.total_served), 1);
  return (
    <div style={R.col}>
      <div style={R.card}>
        <div style={R.cardTitle}>Top Staff Across All Branches</div>
        {staff.length === 0
          ? <div style={{ color: "#475569", fontSize: "13px", padding: "16px" }}>No staff activity in this period.</div>
          : staff.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "14px" }}>
              <div style={{ width: "28px", height: "28px", borderRadius: "7px", background: "rgba(37,99,235,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "800", color: "#60A5FA", flexShrink: 0, marginTop: "2px" }}>#{i+1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <div>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#E2E8F0" }}>{s.staff_name}</span>
                    <span style={{ fontSize: "11px", color: "#475569", marginLeft: "8px" }}>{s.branch_name} · {s.station_name}</span>
                  </div>
                  <span style={{ fontSize: "13px", color: "#60A5FA", fontWeight: "700" }}>{s.total_served} served</span>
                </div>
                <div style={R.barTrack}>
                  <div style={{ ...R.barFill, width: `${(s.total_served / maxServed) * 100}%`, background: COLORS[i % COLORS.length] }} />
                </div>
                <div style={{ fontSize: "11px", color: "#475569", marginTop: "3px" }}>Avg service: {s.avg_service_minutes} min</div>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ── Hourly Heatmap ────────────────────────────────────────────────────────────
function HourlyHeatmap({ data }) {
  const hourly       = data?.hourly || {};
  const branchNames  = data?.branch_names || {};
  const branchIds    = Object.keys(branchNames);
  const hours        = Array.from({ length: 24 }, (_, i) => i);
  const allCounts    = Object.values(hourly).flatMap(h => Object.values(h).map(Number));
  const maxCount     = Math.max(...allCounts, 1);

  const heat = (count) => {
    const pct = count / maxCount;
    if (pct === 0)    return "rgba(30,41,59,0.4)";
    if (pct < 0.25)   return "rgba(37,99,235,0.25)";
    if (pct < 0.5)    return "rgba(37,99,235,0.5)";
    if (pct < 0.75)   return "rgba(37,99,235,0.75)";
    return "#2563EB";
  };

  return (
    <div style={R.col}>
      <div style={R.card}>
        <div style={R.cardTitle}>Hourly Volume Heatmap — All Branches</div>
        <p style={{ fontSize: "13px", color: "#64748B", marginBottom: "16px" }}>Darker = more tickets. Shows which hours are busiest at each branch.</p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: "11px", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ padding: "6px 10px", color: "#475569", textAlign: "left", fontWeight: "700" }}>Branch</th>
                {hours.map(h => <th key={h} style={{ padding: "4px 6px", color: "#475569", textAlign: "center", fontWeight: "600", minWidth: "28px" }}>{h}h</th>)}
              </tr>
            </thead>
            <tbody>
              {branchIds.map(bid => (
                <tr key={bid}>
                  <td style={{ padding: "4px 10px", color: "#94A3B8", fontWeight: "600", whiteSpace: "nowrap" }}>
                    {branchNames[bid]}
                  </td>
                  {hours.map(h => {
                    const count = hourly[h]?.[bid] || 0;
                    return (
                      <td key={h} style={{ padding: "3px 4px" }}>
                        <div style={{ width: "24px", height: "24px", borderRadius: "4px", background: heat(count), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px", color: count > maxCount * 0.5 ? "#fff" : "transparent", fontWeight: "700", margin: "0 auto" }} title={`${branchNames[bid]} at ${h}:00 — ${count} tickets`}>
                          {count || ""}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {branchIds.length === 0 && (
                <tr><td colSpan={25} style={{ textAlign: "center", color: "#475569", padding: "32px" }}>No data for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function thisMonthStart() {
  const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0];
}
function today() { return new Date().toISOString().split("T")[0]; }

const styles = {
  root:      { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1200px" },
  toolbar:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" },
  tabs:      { display: "flex", gap: "4px", flexWrap: "wrap" },
  tab:       { background: "none", border: "none", borderBottom: "2px solid transparent", padding: "8px 14px", fontSize: "13px", fontWeight: "500", color: "#64748B", cursor: "pointer", fontFamily: "inherit" },
  tabActive: { color: "#60A5FA", borderBottomColor: "#2563EB" },
  datePicker:{ background: "rgba(30,41,59,0.8)", border: "1px solid rgba(51,65,85,0.8)", borderRadius: "8px", padding: "7px 10px", color: "#F1F5F9", fontSize: "13px", outline: "none", fontFamily: "inherit" },
};

const R = {
  col:      { display: "flex", flexDirection: "column", gap: "16px" },
  statsRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  card:     { background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.12)", borderRadius: "12px", padding: "18px 20px" },
  cardTitle:{ fontSize: "13px", fontWeight: "700", color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "16px" },
  barTrack: { height: "6px", background: "rgba(51,65,85,0.4)", borderRadius: "3px", overflow: "hidden" },
  barFill:  { height: "100%", borderRadius: "3px", transition: "width 0.4s" },
  table:    { width: "100%", borderCollapse: "collapse" },
  th:       { padding: "9px 12px", fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid rgba(37,99,235,0.1)" },
  tr:       { borderBottom: "1px solid rgba(37,99,235,0.05)" },
  td:       { padding: "11px 12px", fontSize: "13px", color: "#94A3B8", verticalAlign: "middle" },
};
